import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import { toast } from 'react-toastify';

function LeadForm({ lead, onSubmit, onCancel, loading }) {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    platform: 'manual',
    campaign_name: '',
    adset_name: '',
    ad_name: '',
    propertyType: '',
    projectType: '',
    budgetMin: '',
    budgetMax: '',
    squareFootage: '',
    roomCount: '',
    preferredStyle: '',
    roomTypes: '',
    address: '',
    timeframe: '',
    initialNotes: ''
  });

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        phoneNumber: lead.phoneNumber || '',
        email: lead.email || '',
        platform: lead.platform || 'manual',
        campaign_name: lead.campaign_name || '',
        adset_name: lead.adset_name || '',
        ad_name: lead.ad_name || '',
        propertyType: lead.propertyType || '',
        projectType: lead.projectType || '',
        budgetMin: lead.budgetMin || '',
        budgetMax: lead.budgetMax || '',
        squareFootage: lead.squareFootage || '',
        roomCount: lead.roomCount || '',
        preferredStyle: lead.preferredStyle || '',
        roomTypes: lead.roomTypes || '',
        address: lead.address || '',
        timeframe: lead.timeframe || '',
        initialNotes: lead.initialNotes || ''
      });
    }
  }, [lead]);

  function handleChange(e) {
    const { name, value } = e.target;

    // Auto-format phone number
    if (name === 'phoneNumber') {
      const cleaned = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: cleaned
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    // Validation - phone number and name are required
    if (!formData.phoneNumber.trim()) {
      toast.error('Phone number is required');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Validate email format if provided
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    onSubmit(formData);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-tenq-gold">Basic Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="input"
              placeholder="60123456789"
              required
              disabled={!!lead} // Can't change phone number when editing
            />
          </div>

          <div>
            <label className="label">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input"
              placeholder="John Doe"
              required
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="label">Email (optional)</label>
          <input
            type="text"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input"
            placeholder="john@example.com"
          />
        </div>
      </div>

      {/* Platform & Campaign */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-tenq-gold">Source Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Platform</label>
            <select
              name="platform"
              value={formData.platform}
              onChange={handleChange}
              className="input"
            >
              <option value="manual">Manual</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="google">Google</option>
              <option value="referral">Referral</option>
            </select>
          </div>

          <div>
            <label className="label">Campaign Name</label>
            <input
              type="text"
              name="campaign_name"
              value={formData.campaign_name}
              onChange={handleChange}
              className="input"
              placeholder="Summer Promo 2026"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="label">Ad Set Name</label>
            <input
              type="text"
              name="adset_name"
              value={formData.adset_name}
              onChange={handleChange}
              className="input"
            />
          </div>

          <div>
            <label className="label">Ad Name</label>
            <input
              type="text"
              name="ad_name"
              value={formData.ad_name}
              onChange={handleChange}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Interior Design Details */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-tenq-gold">Project Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Property Type</label>
            <select
              name="propertyType"
              value={formData.propertyType}
              onChange={handleChange}
              className="input"
            >
              <option value="">Select property type</option>
              <option value="hdb">HDB</option>
              <option value="condo">Condo</option>
              <option value="landed">Landed</option>
              <option value="commercial">Commercial</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="label">Project Type</label>
            <select
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              className="input"
            >
              <option value="">Select project type</option>
              <option value="full_renovation">Full Renovation</option>
              <option value="partial_renovation">Partial Renovation</option>
              <option value="interior_design">Interior Design</option>
              <option value="consultation">Consultation</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="label">Budget Min (RM)</label>
            <input
              type="number"
              name="budgetMin"
              value={formData.budgetMin}
              onChange={handleChange}
              className="input"
              placeholder="50000"
              min="0"
            />
          </div>

          <div>
            <label className="label">Budget Max (RM)</label>
            <input
              type="number"
              name="budgetMax"
              value={formData.budgetMax}
              onChange={handleChange}
              className="input"
              placeholder="100000"
              min="0"
            />
          </div>

          <div>
            <label className="label">Square Footage</label>
            <input
              type="number"
              name="squareFootage"
              value={formData.squareFootage}
              onChange={handleChange}
              className="input"
              placeholder="1000"
              min="0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="label">Room Count</label>
            <input
              type="text"
              name="roomCount"
              value={formData.roomCount}
              onChange={handleChange}
              className="input"
              placeholder="3 bedrooms, 2 bathrooms"
            />
          </div>

          <div>
            <label className="label">Preferred Style</label>
            <input
              type="text"
              name="preferredStyle"
              value={formData.preferredStyle}
              onChange={handleChange}
              className="input"
              placeholder="Modern, Minimalist, Scandinavian"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="label">Room Types</label>
            <input
              type="text"
              name="roomTypes"
              value={formData.roomTypes}
              onChange={handleChange}
              className="input"
              placeholder="Living room, Kitchen, Master bedroom"
            />
          </div>

          <div>
            <label className="label">Timeframe</label>
            <input
              type="text"
              name="timeframe"
              value={formData.timeframe}
              onChange={handleChange}
              className="input"
              placeholder="3-6 months"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="label">Address</label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="input"
            rows="2"
            placeholder="123 Main Street, Singapore 123456"
          />
        </div>

        <div className="mt-4">
          <label className="label">Initial Notes</label>
          <textarea
            name="initialNotes"
            value={formData.initialNotes}
            onChange={handleChange}
            className="input"
            rows="3"
            placeholder="Any additional information..."
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
        >
          {lead ? 'Update Lead' : 'Create Lead'}
        </Button>
      </div>
    </form>
  );
}

export default LeadForm;
